"use client";

import { useRef } from "react";
import { ArrowLeft, Calendar, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts, Post } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { useReadableArticleText } from "@/hooks/useReadableArticleText";

function SinglePostContent({ initialPost }: { initialPost?: Post }) {
  const { posts, loading, error } = usePosts();
  const articleContentRef = useRef<HTMLDivElement | null>(null);

  const post = initialPost || posts[0];
  useReadableArticleText(articleContentRef, [post?.content, post?.excerpt]);

  if (!initialPost) {
    if (loading) {
      return (
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="h-12 bg-muted/40 rounded-md animate-pulse" />
          <div className="h-96 bg-muted/40 rounded-md animate-pulse" />
          <div className="space-y-3">
            <div className="h-4 bg-muted/40 rounded animate-pulse" />
            <div className="h-4 bg-muted/40 rounded animate-pulse w-5/6" />
            <div className="h-4 bg-muted/40 rounded animate-pulse w-4/6" />
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="max-w-4xl mx-auto text-center py-16">
          <p className="text-muted-foreground font-default">Makale yüklenirken bir hata oluştu.</p>
        </div>
      );
    }

    if (posts.length === 0) {
      return (
        <div className="max-w-4xl mx-auto text-center py-16">
          <p className="text-muted-foreground font-default">Makale bulunamadı.</p>
        </div>
      );
    }
  }

  if (!post) {
    return null;
  }

  const authorName = post.author?.name || "Av. Arda Can Şahin";
  const authorAvatar = post.author?.avatar?.["96"] || post.author?.avatar?.["48"];
  const authorInitials = authorName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("tr-TR", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <Link to="https://ardacansahin.av.tr/bilgi-merkezi/">
          <Button variant="ghost" size="sm" className="font-default text-white hover:text-white hover:bg-transparent">
            <ArrowLeft className="size-4" />
            Geri Dön
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-8">
        <div className="lg:col-span-7 lg:pt-0">
          {post.categories && post.categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {post.categories.slice(0, 3).map((cat, i) => (
                <span
                  data-index={i}
                  key={cat.id}
                  className="font-default text-xs uppercase tracking-widest text-white px-3 py-1.5 border border-primary rounded-md bg-primary"
                >
                  {cat.name}
                </span>
              ))}
            </div>
          )}

          <h1
            className="font-default font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-white mb-4"
            dangerouslySetInnerHTML={{ __html: post.title }}
          />

          <div className="hidden flex-wrap items-center gap-5 text-sm text-muted-foreground font-default">
            <div className="flex items-center gap-3">
              <Avatar className="size-10 border border-border">
                {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
                <AvatarFallback className="bg-secondary text-primary font-default text-xs">
                  {authorInitials}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="text-primary font-medium">{authorName}</span>
                <span className="text-xs">Yazar</span>
              </div>
            </div>

            {formattedDate && (
              <>
                <Separator orientation="vertical" className="h-8 hidden sm:block bg-border/50" />
                <div className="flex items-center gap-2">
                  <Calendar className="size-4" />
                  <span>{formattedDate}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <article className="lg:col-span-8 lg:col-start-2 bg-white text-justify text-[#1F2937] p-6 rounded-lg">
          <div
            ref={articleContentRef}
            className={`article-content font-default text-[12px] sm:text-[13px] leading-[1.75] prose prose-headings:font-default prose-headings:font-medium prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-3 prose-p:mb-6 prose-a:underline prose-a:underline-offset-4 prose-strong:font-semibold prose-blockquote:border-l-2 prose-blockquote:border-primary prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:font-default prose-blockquote:text-xl prose-blockquote:my-8 prose-ul:my-6 prose-li:mb-2 [&_h2]:font-default [&_h2]:font-medium [&_h2]:text-3xl [&_h2]:mt-12 [&_h2]:mb-4 [&_h3]:font-default [&_h3]:font-medium [&_h3]:text-2xl [&_h3]:mt-8 [&_h3]:mb-3 [&_p]:mb-6 [&_a]:underline [&_a]:underline-offset-4 [&_blockquote]:border-l-2 [&_blockquote]:border-primary [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:font-default [&_blockquote]:text-xl [&_blockquote]:my-8 [&_ul]:my-6 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-2`}
            dangerouslySetInnerHTML={{ __html: post.content || post.excerpt || "" }}
          />

          {post.tags && post.tags.length > 0 && (
            <div className="mt-4 pt-2 border-t border-border/50 bg-white rounded-lg p-6">
              <p className="font-default text-xs uppercase tracking-widest text-[#1F2937] mb-4">
                Etiketler
              </p>
              <div className="flex flex-wrap gap-1">
                {post.tags.map((tag, i) => (
                  <span
                    data-index={i}
                    key={tag.id}
                    className="font-default text-sm px-3 py-1.5 bg-white text-[#1F2937] rounded-md border border-primary"
                  >
                    #{tag.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </article>
      </div>
    </div>
  );
}

function RelatedPostsContent() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="space-y-4">
            <div className="h-6 bg-muted/40 rounded animate-pulse" />
            <div className="h-4 bg-muted/40 rounded animate-pulse w-3/4" />
          </div>
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <p className="text-muted-foreground font-default text-center py-8">
        İlgili makale bulunamadı.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {posts.slice(0, 3).map((post, i) => (
        <Card key={post.id} data-index={i} className="bg-background border-border/50 rounded-md hover:border-primary/20 transition-colors duration-300 h-full py-6">
          <Link
            to={post.link || "#"}
            className="group block h-full"
          >
            <CardContent className="space-y-2">
              {post.categories && post.categories[0] && (
                <p className="font-default text-xs uppercase tracking-widest text-white">
                  {post.categories[0].name}
                </p>
              )}
              <h3
                className="font-default font-medium text-xl leading-snug text-white group-hover:text-white/80 transition-colors"
                dangerouslySetInnerHTML={{ __html: post.title }}
              />
              {post.excerpt && (
                <div
                  className="font-default text-sm text-muted-foreground line-clamp-2"
                  dangerouslySetInnerHTML={{ __html: post.excerpt }}
                />
              )}
              <div className="hidden">
                <User className="size-3" />
                <span>{post.author?.name || "Av. Arda Can Şahin"}</span>
              </div>
            </CardContent>
          </Link>
        </Card>
      ))}
    </div>
  );
}

export default function SinglePostTemplateSection({ initialPost }: { initialPost?: Post }) {
  const currentWPQueryParams = (window as any).wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  const relatedQuery = new WP_Query({
    post_type: currentWPQueryParams?.post_type || "post",
    per_page: 3,
    orderby: "date",
    order: "DESC",
  });

  return (
    <section data-section-id="4793"
      id="single-post-template-section"
      className="bg-background text-foreground py-12 sm:py-16 lg:py-20"
    >
      <div className="container mx-auto px-8 lg:px-16">
        <PostsProvider wp_query={wp_query}>
          <SinglePostContent initialPost={initialPost} />
        </PostsProvider>

        <div className="max-w-6xl mx-auto mt-6 lg:mt-8">
          <div className="flex items-end justify-between mb-4 flex-wrap gap-4">
            <div>
              <p className="font-default text-xs uppercase tracking-widest text-white mb-3">
                Okumaya Devam Edin
              </p>
              <h2 className="font-default font-medium text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">
                İlgili Makaleler
              </h2>
            </div>
            <Separator className="flex-1 ml-8 hidden sm:block bg-border/50" />
          </div>

          <PostsProvider wp_query={relatedQuery}>
            <RelatedPostsContent />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}